import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import EventFilterSection from './components/EventFilterSection';
import { clubEvents } from '../../mocks/events';

// ── AEO: Event date parser ────────────────────────────────────────────────
// Handles formats: '1/1（四）' | '3/14-15（六、日）' | '6/18-21' | '12月'
function parseEventDate(dateStr: string): { startDate: string; endDate?: string } {
  const cleaned = dateStr.replace(/（[^）]+）/g, '').trim();

  // e.g. "12月"
  if (/^\d+月$/.test(cleaned)) {
    const month = parseInt(cleaned, 10);
    return { startDate: `2026-${String(month).padStart(2, '0')}-01` };
  }

  // e.g. "1/1", "3/14-15", "6/18-21"
  const rangeMatch = cleaned.match(/^(\d+)\/(\d+)(?:-(\d+))?$/);
  if (rangeMatch) {
    const mm = String(parseInt(rangeMatch[1], 10)).padStart(2, '0');
    const dd1 = String(parseInt(rangeMatch[2], 10)).padStart(2, '0');
    if (rangeMatch[3]) {
      const dd2 = String(parseInt(rangeMatch[3], 10)).padStart(2, '0');
      return { startDate: `2026-${mm}-${dd1}`, endDate: `2026-${mm}-${dd2}` };
    }
    return { startDate: `2026-${mm}-${dd1}` };
  }

  return { startDate: '2026-01-01' };
}

// ── AEO: Build Event @graph from mock data ────────────────────────────────
const eventListSchema = {
  '@context': 'https://schema.org',
  '@graph': clubEvents.map((ev) => {
    const { startDate, endDate } = parseEventDate(ev.date);
    return {
      '@type': 'Event',
      '@id': `https://ymctaipei.com/annual-plan#event-${ev.id}`,
      name: ev.title,
      description: ev.description,
      startDate,
      ...(endDate ? { endDate } : {}),
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: {
        '@type': 'Place',
        name: ev.location,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '台北市',
          addressRegion: 'TW-TPE',
          addressCountry: 'TW',
        },
      },
      organizer: {
        '@type': 'Organization',
        name: '台北市青年企業研究社 YMC Taipei',
        url: 'https://ymctaipei.com/',
      },
      url: 'https://ymctaipei.com/annual-plan',
      inLanguage: 'zh-TW',
      ...(ev.director
        ? {
            performer: {
              '@type': 'Person',
              name: ev.director,
              jobTitle: '輪值理事',
            },
          }
        : {}),
    };
  }),
};

const annualPlanSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'YMC Taipei 2026 年度計畫活動總覽',
  description: '2026年 YMC Taipei 台北市青年企業研究社全年30+場精彩活動，涵蓋月例會、聯誼會、全國社活動等多元類型。',
  url: 'https://ymctaipei.com/annual-plan',
  publisher: {
    '@type': 'Organization',
    name: '台北市青年企業研究社 YMC Taipei',
  },
  inLanguage: 'zh-TW',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['h1', 'h2'],
  },
};

const annualPlanBreadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首頁', item: 'https://ymctaipei.com/' },
    { '@type': 'ListItem', position: 2, name: '年度計畫', item: 'https://ymctaipei.com/annual-plan' },
  ],
};

export default function AnnualPlanPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* React 19 document metadata hoisting */}
      <title>2026 年度計畫 | 台北市青年企業研究社 YMC Taipei</title>
      <meta name="description" content="YMC Taipei 2026 年度計畫，全年共30+場精彩活動，涵蓋企業講座、聯誼活動、全國社活動與海外考察，與青年企業家共同成長、拓展人脈。" />
      <meta name="keywords" content="YMC Taipei 2026活動,台北青企社年度計畫,青年企業家活動,企業家講座,月例會" />
      <link rel="canonical" href="https://ymctaipei.com/annual-plan" />
      <meta property="og:title" content="YMC Taipei 2026 年度計畫 | 30+場精彩活動" />
      <meta property="og:description" content="2026年 YMC Taipei 全年活動總覽，月例會、聯誼會、全國社活動一網打盡。" />
      <meta property="og:url" content="https://ymctaipei.com/annual-plan" />
      <meta property="og:type" content="website" />

      {/* AEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(annualPlanSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(annualPlanBreadcrumbSchema) }}
      />
      {/* AEO: Individual Event Schemas for all 37 activities */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(eventListSchema) }}
      />

      <Navbar />

      {/* Page Hero */}
      <section className="relative h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/b66381d951bb0d89f07ae4b43284dca6.jpg"
          alt="年度計畫"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 sm:px-6 pt-20">
          <p className="text-sm sm:text-base font-medium text-orange-300 tracking-widest uppercase mb-4">2026 Annual Plan</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 drop-shadow-lg">
            年度計畫
          </h1>
          <p className="text-lg sm:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            2026 年 YMC Taipei 精心規劃全年 30+ 場精彩活動，涵蓋企業參訪、國際交流、講座研討與聯誼活動，與優秀的青年企業家共同成長。
          </p>
        </div>
      </section>

      {/* Filterable Events */}
      <EventFilterSection />

      <Footer />
    </div>
  );
}
